/**
 * Date and time utility functions
 * Used across the application for consistent date/time formatting
 */

/**
 * Parse date strings as local dates (not UTC)
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Date|null} Local date object or null if invalid
 */
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null
  const [year, month, day] = dateStr.split('-')
  return new Date(year, month - 1, day)
}

/**
 * Format time in 12-hour format with AM/PM
 * @param {string} timeStr - Time string in HH:MM format
 * @returns {string} Formatted time string (e.g., "2:30 PM")
 */
export const formatTime = (timeStr) => {
  if (!timeStr) return ''
  const [hours, minutes] = timeStr.split(':')
  const hour = parseInt(hours)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}

/**
 * Format date in readable format
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date string (e.g., "Jan 15, 2024")
 */
export const formatDate = (date) => {
  if (!date) return ''
  const dateObj = typeof date === 'string' ? parseLocalDate(date) : date
  if (!dateObj) return ''

  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

/**
 * Check if a date is in the future
 * @param {Date|string} date - Date object or date string
 * @returns {boolean} True if date is in the future
 */
export const isUpcoming = (date) => {
  if (!date) return false
  const dateObj = typeof date === 'string' ? parseLocalDate(date) : date
  if (!dateObj) return false

  return dateObj > new Date()
}

/**
 * Sort dates in ascending order
 * @param {string} dateA - First date string
 * @param {string} dateB - Second date string
 * @returns {number} Comparison result for sort
 */
export const sortByDate = (dateA, dateB) => {
  const a = parseLocalDate(dateA)
  const b = parseLocalDate(dateB)
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  return a - b
}

/**
 * Format date and time together
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} timeStr - Time string in HH:MM format
 * @returns {string} Formatted date and time string (e.g., "Jan 15, 2024 at 2:30 PM")
 */
export const formatDateTime = (dateStr, timeStr) => {
  const date = formatDate(dateStr)
  const time = formatTime(timeStr)
  if (!date && !time) return ''
  if (!date) return time
  if (!time) return date
  return `${date} at ${time}`
}

/**
 * Format a date/time as relative time (e.g., "2 hours ago", "3 days ago")
 * @param {Date|string} date - Date object or ISO date string
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return ''

  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ''

  const now = new Date()
  const diffMs = now - dateObj
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)

  if (diffSeconds < 60) {
    return 'just now'
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
  } else if (diffWeeks < 4) {
    return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`
  } else if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`
  } else {
    return formatDate(dateObj)
  }
}
